import { NavLink, useLocation } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { 
  LayoutDashboard, 
  Workflow, 
  FileText, 
  Plug, 
  LayoutTemplate,
  Settings,
  FolderKanban,
  AlertTriangle,
  X,
  Users,
  ScrollText,
  Shield,
  ChevronDown,
  ChevronRight,
  Database,
  Ticket,
  Bot,
  BarChart3,
  Briefcase,
  PanelLeftClose,
  PanelLeftOpen
} from 'lucide-react';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import logo from '@/assets/images/logo-sidebar.png';
import { getVersionString } from '@/config/version';
import { usePermissions } from '@/hooks/usePermissions';
import { Role, Scope, Resource } from '@/types/rbac';
import { useWhiteLabelStore } from '@/stores/whiteLabelStore';
import { useAuthStore } from '@/stores/authStore';
import { useSidebarStore } from '@/stores/sidebarStore';
import { Tooltip } from '@/components/ui/Tooltip';
import { businessEntitiesApi } from '@/api/businessEntities';
import type { LucideIcon } from 'lucide-react';

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
}

interface NavItem {
  name: string;
  path: string;
  icon: LucideIcon;
  requiredRole?: Role;
  resource?: Resource;
  scope?: Scope;
  children?: NavItem[];
}

export const Sidebar = ({ isOpen, onClose }: SidebarProps) => {
  const { t } = useTranslation();
  const { hasRole, hasScope } = usePermissions();
  const { companyName, logoUrl: customLogoUrl, primaryColor, accentColor } = useWhiteLabelStore();
  const { accountId } = useAuthStore();
  const { isCollapsed, toggleCollapsed } = useSidebarStore();
  const location = useLocation();
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>({});

  // Entidades de negócio com "Gerar Tela Dinâmica" ativado — viram submenus de "Gestão".
  const [screenEntities, setScreenEntities] = useState<Array<{ id: string; name: string }>>([]);

  useEffect(() => {
    let cancelled = false;

    // Só carrega se o usuário tem acesso a Entidades de Negócio.
    if (!hasScope(Resource.BusinessEntities, Scope.LIST)) {
      setScreenEntities([]);
      return;
    }

    businessEntitiesApi
      .list()
      .then((entities) => {
        if (cancelled) return;
        const withScreen = (entities || [])
          .filter((e) => e.generateScreen)
          .map((e) => ({ id: e.id, name: e.name }));
        setScreenEntities(withScreen);
      })
      .catch((err) => {
        console.error('Erro ao carregar entidades para o menu Gestão:', err);
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountId]);

  // Grupos são expandidos por padrão; só ficam recolhidos quando explicitamente alternados.
  const isGroupExpanded = (path: string) => expandedGroups[path] !== false;
  const toggleGroup = (path: string) =>
    setExpandedGroups((prev) => ({ ...prev, [path]: prev[path] === false }));

  /**
   * Verifica se um link (que pode conter query string) está ativo.
   * Necessário para os submenus de tela dinâmica, que compartilham o mesmo
   * pathname (/entity-screen) e se diferenciam apenas pela query (entity_id).
   */
  const isChildLinkActive = (path: string): boolean => {
    const [pathname, query] = path.split('?');
    if (location.pathname !== pathname) {
      return false;
    }
    if (!query) {
      return true;
    }
    const target = new URLSearchParams(query);
    const current = new URLSearchParams(location.search);
    for (const [key, value] of target.entries()) {
      if (current.get(key) !== value) {
        return false;
      }
    }
    return true;
  };

  const membersPath = accountId ? `/members?accountId=${accountId}` : '/members';

  const navItems: NavItem[] = [
    {
      name: t('navigation.dashboard'),
      path: '/dashboard',
      icon: LayoutDashboard,
    },
    {
      name: t('navigation.credentials'),
      path: '/credentials',
      icon: Plug,
      resource: Resource.ApiCredentials,
      scope: Scope.LIST,
    },
    {
      name: t('navigation.projects'),
      path: '/projects',
      icon: FolderKanban,
      resource: Resource.Projects,
      scope: Scope.LIST,
    },
    {
      name: t('navigation.builders', 'Construtores'),
      path: '/flows',
      icon: Workflow,
      resource: Resource.Flows,
      scope: Scope.LIST,
      children: [
        {
          name: t('navigation.workflows'),
          path: '/flows',
          icon: Workflow,
          resource: Resource.Flows,
          scope: Scope.LIST,
        },
        {
          name: t('navigation.entities', 'Entidades'),
          path: '/business-entities',
          icon: Database,
          resource: Resource.BusinessEntities,
          scope: Scope.LIST,
        },
        {
          name: t('navigation.agents', 'Agentes'),
          path: '/agents',
          icon: Bot,
          resource: Resource.Agents,
          scope: Scope.LIST,
        },
        {
          name: t('navigation.dataAnalytics', 'Data & Analytics'),
          path: '/data-analytics',
          icon: BarChart3,
          resource: Resource.Flows,
          scope: Scope.LIST,
        },
      ],
    },
    {
      name: t('navigation.integrationErrors', 'Mapeamento de Erros'),
      path: '/integration-errors',
      icon: AlertTriangle,
      resource: Resource.Executions,
      scope: Scope.READ,
    },
    {
      name: t('navigation.reports'),
      path: '/reports',
      icon: FileText,
      resource: Resource.Executions,
      scope: Scope.LIST,
    },
    {
      name: t('navigation.templates'),
      path: '/templates',
      icon: LayoutTemplate,
      resource: Resource.Flows,
      scope: Scope.LIST,
    },
    // Admin panel (only ADMIN)
    {
      name: t('rbac.nav.adminPanel'),
      path: '/admin',
      icon: Shield,
      requiredRole: Role.ADMIN,
    },
    // Settings with sub-items
    {
      name: t('navigation.settings'),
      path: '/settings',
      icon: Settings,
      requiredRole: Role.GERENTE,
      children: [
        {
          name: t('rbac.nav.members'),
          path: membersPath,
          icon: Users,
          requiredRole: Role.GERENTE,
          resource: Resource.Members,
          scope: Scope.LIST,
        },
        {
          name: t('rbac.nav.auditLogs'),
          path: '/audit-logs',
          icon: ScrollText,
          requiredRole: Role.GERENTE,
          resource: Resource.Members,
          scope: Scope.LIST,
        },
      ],
    },
    // Gestão — submenus dinâmicos: uma tela por Entidade de Negócio com "Gerar Tela Dinâmica".
    {
      name: t('navigation.management', 'Gestão'),
      path: '/gestao',
      icon: Briefcase,
      children: screenEntities.map((entity) => ({
        name: entity.name,
        path: `/entity-screen?entity_id=${entity.id}`,
        icon: Database,
        resource: Resource.BusinessEntities,
        scope: Scope.LIST,
      })),
    },
    // Suporte (any authenticated member)
    {
      name: 'Suporte',
      path: hasRole(Role.ADMIN) ? '/admin/tickets' : '/tickets',
      icon: Ticket,
    },
  ];

  // Filter nav items based on user permissions
  const isItemVisible = (item: NavItem): boolean => {
    if (item.requiredRole && !hasRole(item.requiredRole)) {
      return false;
    }
    if (item.resource && item.scope && !hasScope(item.resource, item.scope)) {
      return false;
    }
    // For parent items with children, only show if at least one child is visible
    if (item.children && item.children.length > 0) {
      return item.children.some(isItemVisible);
    }
    return true;
  };

  const visibleNavItems = navItems.filter(isItemVisible);

  return (
    <>
      {/* Mobile overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-gray-900/50 z-40 lg:hidden"
          onClick={onClose}
          aria-hidden="true"
        />
      )}

      {/* Sidebar */}
      <aside
        className={clsx(
          'fixed top-0 left-0 h-screen transform transition-all duration-300 ease-in-out',
          isCollapsed ? 'w-[72px]' : 'w-64',
          isOpen ? 'translate-x-0 z-50' : '-translate-x-full lg:translate-x-0 lg:z-30'
        )}
        style={{
          backgroundColor: '#FFF',
          borderRight: '1px solid #E6E6F0'
        }}
      >
        {/* Logo, toggle and close button */}
        <div 
          className={clsx(
            'flex items-center h-20 relative',
            isCollapsed ? 'justify-center' : 'justify-between px-3'
          )}
          style={{ borderBottom: '1px solid #E6E6F0' }}
        >
          {isCollapsed ? (
            <div className="flex flex-col items-center gap-1">
              <img 
                src={customLogoUrl || logo} 
                alt={`${companyName} Logo`} 
                className="h-10 w-10 object-contain rounded"
              />
              <button
                onClick={toggleCollapsed}
                className="p-1 rounded-lg hover:bg-gray-100 transition-colors hidden lg:flex"
                aria-label="Expandir menu"
                title="Expandir menu"
              >
                <PanelLeftOpen className="w-4 h-4" style={{ color: 'var(--wl-text-muted)' }} />
              </button>
            </div>
          ) : (
            <>
              <div className="flex-1 flex justify-center">
                {customLogoUrl ? (
                  <img 
                    src={customLogoUrl} 
                    alt={`${companyName} Logo`} 
                    className="h-16 w-auto object-contain max-w-[200px]"
                  />
                ) : (
                  <img 
                    src={logo} 
                    alt="FeedBatch Logo" 
                    className="h-24 w-auto object-contain"
                    style={{ margin: '-40px 0' }}
                  />
                )}
              </div>
              <button
                onClick={toggleCollapsed}
                className="p-2 rounded-lg hover:bg-gray-100 transition-colors hidden lg:flex"
                aria-label="Recolher menu"
                title="Recolher menu"
              >
                <PanelLeftClose className="w-4 h-4" style={{ color: 'var(--wl-text-muted)' }} />
              </button>
            </>
          )}
          <button
            onClick={onClose}
            className="absolute right-2 top-2 p-2 rounded-lg transition-colors lg:hidden hover:bg-gray-100"
            aria-label="Close menu"
          >
            <X 
              className="w-5 h-5"
              style={{ color: 'var(--wl-text-muted)' }}
            />
          </button>
        </div>

        {/* Navigation */}
        <nav className={clsx('py-1 space-y-1 overflow-x-hidden overflow-y-auto', isCollapsed ? 'px-1' : 'px-2')} style={{ maxHeight: 'calc(100vh - 80px - 80px)' }}>
          {visibleNavItems.map((item) => {
            const Icon = item.icon;
            const hasChildren = item.children && item.children.length > 0;
            const visibleChildren = hasChildren 
              ? item.children!.filter(isItemVisible) 
              : [];
            const isSettingsGroup = hasChildren && visibleChildren.length > 0;
            const isChildActive = visibleChildren.some(child => {
              const childPathname = child.path.split('?')[0];
              return location.pathname.startsWith(childPathname);
            });
            const isParentActive = location.pathname === item.path || isChildActive;

            if (isSettingsGroup) {
              // Collapsed mode: show only parent icon with tooltip
              if (isCollapsed) {
                // Modo recolhido: mostra apenas os ícones dos filhos (sem o ícone do pai),
                // cada um como um link independente com tooltip.
                return (
                  <div key={item.path} className="space-y-1">
                    {visibleChildren.map((child) => {
                      const ChildIcon = child.icon;
                      const isChildItemActive = isChildLinkActive(child.path);
                      return (
                        <Tooltip key={child.path} content={child.name} position="right" delay={0} className="w-full">
                          <NavLink
                            to={child.path}
                            onClick={() => {
                              if (window.innerWidth < 1024) onClose();
                            }}
                            className="flex items-center justify-center p-2.5 rounded-lg transition-all duration-200"
                            style={{
                              backgroundColor: isChildItemActive ? `${primaryColor}14` : 'transparent',
                            }}
                            onMouseEnter={(e) => {
                              if (!isChildItemActive) {
                                e.currentTarget.style.backgroundColor = 'rgba(50, 71, 92, 0.04)';
                              }
                            }}
                            onMouseLeave={(e) => {
                              if (!isChildItemActive) {
                                e.currentTarget.style.backgroundColor = 'transparent';
                              }
                            }}
                          >
                            <ChildIcon
                              className="w-[18px] h-[18px]"
                              style={{ color: isChildItemActive ? accentColor : 'var(--wl-text-muted)' }}
                            />
                          </NavLink>
                        </Tooltip>
                      );
                    })}
                  </div>
                );
              }

              return (
                <div key={item.path}>
                  {/* Parent item (clickable to navigate + expand) */}
                  <div className="flex items-center">
                    <NavLink
                      to={item.path}
                      onClick={() => {
                        setExpandedGroups((prev) => ({ ...prev, [item.path]: true }));
                        if (window.innerWidth < 1024) onClose();
                      }}
                      style={() => ({
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        padding: '12px 16px',
                        borderRadius: '6px',
                        fontSize: '0.875rem',
                        fontWeight: 500,
                        transition: 'all 0.2s ease-in-out',
                        backgroundColor: isParentActive ? `${primaryColor}14` : 'transparent',
                        color: isParentActive ? primaryColor : '#22212C',
                        flex: 1,
                      })}
                      onMouseEnter={(e) => {
                        if (!isParentActive) {
                          e.currentTarget.style.backgroundColor = 'rgba(50, 71, 92, 0.04)';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!isParentActive) {
                          e.currentTarget.style.backgroundColor = 'transparent';
                        }
                      }}
                    >
                      <Icon
                        className="w-5 h-5"
                        style={{ color: isParentActive ? accentColor : 'var(--wl-text-muted)' }}
                      />
                      <span className="flex-1">{item.name}</span>
                    </NavLink>
                    <button
                      onClick={() => toggleGroup(item.path)}
                      className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
                    >
                      {isGroupExpanded(item.path) ? (
                        <ChevronDown className="w-4 h-4" style={{ color: 'var(--wl-text-muted)' }} />
                      ) : (
                        <ChevronRight className="w-4 h-4" style={{ color: 'var(--wl-text-muted)' }} />
                      )}
                    </button>
                  </div>
                  {/* Children */}
                  {isGroupExpanded(item.path) && (
                    <div className="ml-4 mt-1 space-y-1">
                      {visibleChildren.map((child) => {
                        const ChildIcon = child.icon;
                        // Detecção de ativo considerando a query string (necessário para os
                        // submenus de tela dinâmica, que compartilham o path /entity-screen).
                        const childActive = isChildLinkActive(child.path);
                        return (
                          <NavLink
                            key={child.path}
                            to={child.path}
                            onClick={() => {
                              if (window.innerWidth < 1024) onClose();
                            }}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '10px',
                              padding: '8px 12px',
                              borderRadius: '6px',
                              fontSize: '0.813rem',
                              fontWeight: 500,
                              transition: 'all 0.2s ease-in-out',
                              backgroundColor: childActive ? `${primaryColor}14` : 'transparent',
                              color: childActive ? primaryColor : '#22212C',
                            }}
                            onMouseEnter={(e) => {
                              if (!childActive) {
                                e.currentTarget.style.backgroundColor = 'rgba(50, 71, 92, 0.04)';
                              }
                            }}
                            onMouseLeave={(e) => {
                              if (!childActive) {
                                e.currentTarget.style.backgroundColor = 'transparent';
                              }
                            }}
                          >
                            <ChildIcon
                              className="w-4 h-4"
                              style={{ color: childActive ? accentColor : 'var(--wl-text-muted)' }}
                            />
                            {child.name}
                          </NavLink>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            }

            // Collapsed mode: icon only with tooltip
            if (isCollapsed) {
              return (
                <Tooltip key={item.path} content={item.name} position="right" delay={0} className="w-full">
                  <NavLink
                    to={item.path}
                    onClick={() => {
                      if (window.innerWidth < 1024) onClose();
                    }}
                    className="flex items-center justify-center p-3 rounded-lg transition-all duration-200"
                    style={({ isActive }) => ({
                      backgroundColor: isActive ? `${primaryColor}14` : 'transparent',
                    })}
                    onMouseEnter={(e) => {
                      const isActive = e.currentTarget.getAttribute('aria-current') === 'page';
                      if (!isActive) {
                        e.currentTarget.style.backgroundColor = 'rgba(50, 71, 92, 0.04)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      const isActive = e.currentTarget.getAttribute('aria-current') === 'page';
                      if (!isActive) {
                        e.currentTarget.style.backgroundColor = 'transparent';
                      }
                    }}
                  >
                    {({ isActive }) => (
                      <Icon
                        className="w-5 h-5"
                        style={{ color: isActive ? accentColor : 'var(--wl-text-muted)' }}
                      />
                    )}
                  </NavLink>
                </Tooltip>
              );
            }

            // Expanded mode: icon + text
            return (
              <NavLink
                key={item.path}
                to={item.path}
                onClick={() => {
                  if (window.innerWidth < 1024) onClose();
                }}
                style={({ isActive }) => ({
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  padding: '12px 16px',
                  borderRadius: '6px',
                  fontSize: '0.875rem',
                  fontWeight: 500,
                  transition: 'all 0.2s ease-in-out',
                  backgroundColor: isActive ? `${primaryColor}14` : 'transparent',
                  color: isActive ? primaryColor : '#22212C',
                })}
                onMouseEnter={(e) => {
                  const isActive = e.currentTarget.getAttribute('aria-current') === 'page';
                  if (!isActive) {
                    e.currentTarget.style.backgroundColor = 'rgba(50, 71, 92, 0.04)';
                  }
                }}
                onMouseLeave={(e) => {
                  const isActive = e.currentTarget.getAttribute('aria-current') === 'page';
                  if (!isActive) {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }
                }}
              >
                {({ isActive }) => (
                  <>
                    <Icon
                      className="w-5 h-5"
                      style={{ color: isActive ? accentColor : 'var(--wl-text-muted)' }}
                    />
                    {item.name}
                  </>
                )}
              </NavLink>
            );
          })}
        </nav>

        {/* Footer */}
        <div 
          className="absolute bottom-0 left-0 right-0"
          style={{ borderTop: '1px solid #E6E6F0' }}
        >
          {/* Version info - only in expanded mode */}
          {!isCollapsed && (
            <div className="p-4">
              <div 
                className="text-xs text-center"
                style={{ color: 'var(--wl-text-muted)' }}
              >
                <p>{companyName} iPaaS {getVersionString()}</p>
                <p className="mt-1">© 2024 All rights reserved</p>
                <div className="mt-2 flex justify-center gap-3">
                  <a 
                    href="/politica-de-privacidade" 
                    target="_blank"
                    className="hover:text-blue-600 transition-colors"
                  >
                    Privacidade
                  </a>
                  <span>•</span>
                  <a 
                    href="/termos-de-servico" 
                    target="_blank"
                    className="hover:text-blue-600 transition-colors"
                  >
                    Termos
                  </a>
                </div>
              </div>
            </div>
          )}
        </div>
      </aside>
    </>
  );
};
